/**
 * Permission taxonomy, mirrored from `apps/api/src/config/permissions.ts`.
 *
 * This drives the UI only — the API re-checks every protected route, so a
 * hidden control is also enforced server-side.
 *
 * Modules mirror the approved sidebar: each renders as one nav item gated by
 * `<module>:view`, and one row in the group permission matrix. Keep this file
 * in sync with the API catalog.
 */

import type { PermissionCategory } from '@/types/api'

export const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'approve', 'delete'] as const
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number]

/** Actions deliberately not offered by a module are hidden in the matrix. */
export interface ModuleDefinition {
  code: string
  label: string
  description: string
  actions: readonly PermissionAction[]
}

export const MODULES = [
  {
    code: 'audit_engagements',
    label: 'Audit Engagements',
    description: 'Audit engagements and their fieldwork.',
    actions: ['view', 'create', 'edit', 'approve', 'delete'],
  },
  {
    code: 'audit_plan',
    label: 'Audit Plan',
    description: 'The annual audit plan and its approvals.',
    actions: ['view', 'create', 'edit', 'approve', 'delete'],
  },
  {
    code: 'users',
    label: 'Users',
    description: 'User accounts, groups and account approvals.',
    actions: ['view', 'create', 'edit', 'approve'],
  },
] as const satisfies readonly ModuleDefinition[]

export type ModuleCode = (typeof MODULES)[number]['code']

/** Build a permission code from its module and action. */
export function permissionCode(module: ModuleCode, action: PermissionAction): string {
  return `${module}:${action}`
}

/** Every permission code in the catalog. */
export const ALL_PERMISSION_CODES: string[] = MODULES.flatMap((module) =>
  module.actions.map((action) => permissionCode(module.code, action)),
)

/** Every access category, in the order the UI presents them. */
export const ALL_PERMISSION_CATEGORIES: readonly PermissionCategory[] = ['VIEW', 'CONTROL']

/** Human label per access category, mirroring the API taxonomy. */
export const PERMISSION_CATEGORY_LABELS: Record<PermissionCategory, string> = {
  VIEW: 'Access',
  CONTROL: 'Control',
}

/**
 * Access grouping of a permission code, derived from its action so it stays in
 * step with the API catalog: `view` is Access, everything else is Control.
 */
export function permissionCategory(code: string): PermissionCategory {
  const action = code.split(':')[1]
  return action === 'view' ? 'VIEW' : 'CONTROL'
}

/** Sentence shown before approval when the account still needs a group. */
export function missingPermissionCategoriesMessage(): string {
  return 'Please make sure you add this user in a group via Groups tab before approving.'
}

const ACTION_LABELS: Record<PermissionAction, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  approve: 'Approve',
  delete: 'Delete',
}

/** Column labels for the permission matrix. */
export const ACTION_COLUMN_LABELS: Record<PermissionAction, string> = ACTION_LABELS

/**
 * Friendly, sentence-style label for a permission code, for display in chips:
 * `audit_plan:view` → "Can view · Audit Plan". Falls back to the
 * raw code for anything not in the catalog.
 */
export function formatPermission(code: string): string {
  const [module, action] = code.split(':')
  const definition = MODULES.find((entry) => entry.code === module)
  const verb = ACTION_LABELS[action as PermissionAction]
  if (!verb) return code
  return `Can ${verb.toLowerCase()} · ${definition?.label ?? module}`
}

export function hasPermission(granted: readonly string[] | undefined, code: string): boolean {
  return Boolean(granted?.includes(code))
}

export function hasAnyPermission(granted: readonly string[] | undefined, codes: readonly string[]): boolean {
  return codes.some((code) => hasPermission(granted, code))
}

export function hasAllPermissions(granted: readonly string[] | undefined, codes: readonly string[]): boolean {
  return codes.every((code) => hasPermission(granted, code))
}
